import { useMemo, useState } from "react";
import {
  resolveAssets,
  symbolToSvg,
  symbolToPng,
  multiToPng,
  multiToSvg,
  assetFilename,
  type AssetItem,
  type ExportFormat,
} from "@/lib/symbol-asset";
import { createZip, type ZipFile } from "@/lib/zip";
import { FillColorPicker, DEFAULT_FILL } from "@/components/FillColorPicker";
import { copyText } from "@/lib/copy";

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface Candidate extends AssetItem {
  /** Where this symbol came from — "Tray", "Favorites", "Recent". */
  source: string;
}

interface Props {
  candidates: Candidate[];
}

const PNG_SIZES = [256, 512, 1024] as const;

/**
 * Multi-select bulk exporter: pick symbols, choose SVG/PNG (+ size), and
 * download a single ZIP built entirely in the browser (see src/lib/zip.ts).
 */
export function BulkExport({ candidates }: Props) {
  // De-dupe across sources by slug, keeping the first source seen.
  const items = useMemo(() => {
    const seen = new Map<string, Candidate>();
    for (const c of candidates) if (!seen.has(c.slug)) seen.set(c.slug, c);
    return [...seen.values()];
  }, [candidates]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(items.map((i) => i.slug)));
  const [format, setFormat] = useState<ExportFormat>("both");
  const [pngSize, setPngSize] = useState<number>(512);
  const [color, setColor] = useState<string>(DEFAULT_FILL);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Output: separate files (ZIP) or one combined image of all selected symbols.
  const [output, setOutput] = useState<"files" | "combined">("files");
  const [columns, setColumns] = useState(0); // 0 = single row
  const [busy, setBusy] = useState(false);

  const allSelected = items.length > 0 && selected.size === items.length;
  const chosen = items.filter((i) => selected.has(i.slug));

  function toggle(slug: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(items.map((i) => i.slug)));
  }

  async function buildFiles(list: AssetItem[]): Promise<ZipFile[]> {
    const enc = new TextEncoder();
    const files: ZipFile[] = [];
    const wantSvg = format === "svg" || format === "both";
    const wantPng = format === "png" || format === "both";
    const perItem = (wantSvg ? 1 : 0) + (wantPng ? 1 : 0);
    let done = 0;
    setProgress({ done: 0, total: list.length * perItem });
    for (const item of list) {
      if (wantSvg) {
        files.push({
          name: assetFilename(item.slug, "svg"),
          data: enc.encode(await symbolToSvg(item.symbol, pngSize, color)),
        });
        setProgress({ done: ++done, total: list.length * perItem });
      }
      if (wantPng) {
        const blob = await symbolToPng(item.symbol, pngSize, color);
        files.push({
          name: assetFilename(item.slug, "png"),
          data: new Uint8Array(await blob.arrayBuffer()),
        });
        setProgress({ done: ++done, total: list.length * perItem });
      }
    }
    return files;
  }

  async function download() {
    if (chosen.length === 0 || progress) return;
    setError(null);
    try {
      const files = await buildFiles(chosen);
      const zip = createZip(files);
      const blob = new Blob([zip.buffer as ArrayBuffer], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `coolsymbols-${chosen.length}-symbols.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setProgress(null);
    }
  }

  // Combine the selected symbols into ONE image (row or grid).
  async function downloadCombined(fmt: "png" | "svg") {
    if (chosen.length === 0 || busy) return;
    setError(null);
    setBusy(true);
    try {
      const symbols = chosen.map((c) => c.symbol);
      const stem = `coolsymbols-${chosen.length}-combined`;
      if (fmt === "png") {
        triggerDownload(await multiToPng({ symbols, columns, color }), `${stem}.png`);
      } else {
        const svg = await multiToSvg({ symbols, columns, color });
        triggerDownload(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), `${stem}.svg`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setBusy(false);
    }
  }

  async function copyCombined() {
    if (chosen.length === 0) return;
    await copyText(chosen.map((c) => c.symbol).join(""), { source: "export-pattern" });
  }

  if (items.length === 0) {
    return (
      <p className="mt-8 text-muted-foreground">
        Nothing to export yet. Add symbols to your clipboard tray or favourite a few, then come back
        here to download them all at once.
      </p>
    );
  }

  const combined = output === "combined";

  return (
    <div className="mt-8">
      {/* Output mode: separate files (ZIP) or one combined image of all symbols. */}
      <div className="mb-3 inline-flex rounded-lg border border-border bg-surface/60 p-0.5" role="group" aria-label="Output">
        {([["files", "Separate files"], ["combined", "One image"]] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setOutput(id)}
            aria-pressed={output === id}
            className={
              "rounded-md px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
              (output === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-border bg-surface/40 p-4">
        {!combined && (
          <>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Format</span>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value as ExportFormat)}
                className="rounded-md border border-border bg-surface/60 px-3 py-1.5 text-foreground"
              >
                <option value="both">SVG + PNG</option>
                <option value="svg">SVG only</option>
                <option value="png">PNG only</option>
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">PNG / SVG size</span>
              <select
                value={pngSize}
                onChange={(e) => setPngSize(Number(e.target.value))}
                className="rounded-md border border-border bg-surface/60 px-3 py-1.5 text-foreground"
              >
                {PNG_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}×{s}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {combined && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Columns (0 = one row)</span>
            <input
              type="number"
              min={0}
              max={40}
              value={columns}
              onChange={(e) => setColumns(Math.max(0, Math.min(40, Number(e.target.value) || 0)))}
              className="w-28 rounded-md border border-border bg-surface/60 px-3 py-1.5 text-foreground"
            />
          </label>
        )}

        <div className="w-full sm:w-auto">
          <FillColorPicker color={color} onChange={setColor} />
        </div>

        {combined ? (
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyCombined}
              disabled={chosen.length === 0}
              className="rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-semibold transition hover:bg-secondary disabled:opacity-50"
            >
              Copy as text
            </button>
            <button
              type="button"
              onClick={() => downloadCombined("svg")}
              disabled={chosen.length === 0 || busy}
              className="rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-semibold transition hover:bg-secondary disabled:opacity-50"
            >
              SVG
            </button>
            <button
              type="button"
              onClick={() => downloadCombined("png")}
              disabled={chosen.length === 0 || busy}
              className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Building…" : `Download PNG (${chosen.length})`}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={download}
            disabled={chosen.length === 0 || !!progress}
            className="ml-auto rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {progress
              ? `Building… ${progress.done}/${progress.total}`
              : `Download ZIP (${chosen.length})`}
          </button>
        )}
      </div>

      {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

      <div className="mt-5 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Select symbols</h2>
        <button
          type="button"
          onClick={toggleAll}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          {allSelected ? "Deselect all" : "Select all"}
        </button>
      </div>

      <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {items.map((item) => {
          const on = selected.has(item.slug);
          return (
            <li key={item.slug}>
              <button
                type="button"
                onClick={() => toggle(item.slug)}
                aria-pressed={on}
                className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                  on
                    ? "border-primary/50 bg-primary/10"
                    : "border-border bg-surface/40 hover:border-primary/30"
                }`}
              >
                <span className="text-2xl leading-none">{item.symbol}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-foreground">{item.name}</span>
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    {item.source}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={`text-sm ${on ? "text-primary" : "text-muted-foreground"}`}
                >
                  {on ? "✓" : "+"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export { resolveAssets };
